/* Podmíněné sekce a pole hypotečního dotazníku — čisté funkce.
 *
 * PROČ TO EXISTUJE:
 * Dotazník má přes sto polí, ale žádný klient neodpovídá na všechna. Sólo
 * žadatel nemá spolužadatele, OSVČ nemá zaměstnavatele. Bez podmínek se
 * takový člověk prokousává šestnácti poli o partnerovi, kterého nemá — a
 * hlavně: sekce Spolužadatel u něj navždy hlásí „0 / 16“ a dotazník se na
 * 100 % nedostane nikdy. Poradce pak nepozná, jestli klient sekci přeskočil
 * úmyslně, nebo ji nedodělal.
 *
 * KDE JSOU PRAVIDLA: v backendu (models_hypoteka.py), ne tady. Sem přitečou
 * jako DATA v definici sekcí — každá sekce i pole může mít `podminka`, což je
 * seznam podmínek, které musí platit všechny. Tenhle soubor je jen obecný
 * vyhodnocovač. Kdyby tu byl vlastní seznam pravidel, rozešel by se s
 * backendem při první změně a server by počítal jiný postup, než jaký vidí
 * klient ve formuláři.
 *
 * NEZODPOVĚZENÝ SPÍNAČ ZNAMENÁ NERELEVANTNÍ. Dokud klient neřekne, že bere
 * hypotéku ve dvou, na spolužadatele se neptáme a nepočítáme ho. Pole se
 * `spinac: true` je z podmínky své vlastní sekce vyňaté — jinak by se otázka,
 * která sekci odemyká, schovala sama před sebou.
 */

/** Je odpověď vyplněná? Nula JE odpověď (příjem 0 Kč), prázdný řetězec není.
 *  Tatáž definice jako je_vyplneno() na backendu — kdyby se rozešly, hlásil by
 *  server jiný postup než formulář. */
export function jeVyplneno(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
}

/** Platí VŠECHNY podmínky ze seznamu? Prázdný/chybějící seznam = platí. */
export function splnenaPodminka(podminka, odpovedi) {
  const seznam = Array.isArray(podminka) ? podminka : [];
  for (const c of seznam) {
    if (!c || typeof c !== "object") continue;
    const hodnota = (odpovedi || {})[c.pole];
    if (!jeVyplneno(hodnota)) return false;
    if (Array.isArray(c.je) && !c.je.includes(hodnota)) return false;
    if (Array.isArray(c.neni) && c.neni.includes(hodnota)) return false;
  }
  return true;
}

/** Ptáme se na tohle pole při těchhle odpovědích? */
export function poleRelevantni(sekce, pole, odpovedi) {
  if (!pole) return false;
  if (!pole.spinac && !splnenaPodminka(sekce?.podminka, odpovedi)) return false;
  return splnenaPodminka(pole.podminka, odpovedi);
}

/** Pole jedné sekce, na která se opravdu ptáme (v původním pořadí). */
export function relevantniPoleSekce(sekce, odpovedi) {
  const pole = Array.isArray(sekce?.pole) ? sekce.pole.filter(Boolean) : [];
  return pole.filter((p) => poleRelevantni(sekce, p, odpovedi));
}

/** Má se sekce vůbec kreslit? Sekce se spínačem uvnitř ano — spínač musí jít
 *  zodpovědět. Sekce bez jediného relevantního pole se nekreslí. */
export function sekceRelevantni(sekce, odpovedi) {
  return relevantniPoleSekce(sekce, odpovedi).length > 0;
}

/**
 * Postup vyplnění: kolik z relevantních polí je vyplněných.
 * Zrcadlí spocitej_postup() na backendu.
 *
 * @returns {{vyplnenych: number, celkem: number, sekce: Object}}
 */
export function spocitejPostup(sekceSeznam, odpovedi) {
  const seznam = Array.isArray(sekceSeznam) ? sekceSeznam.filter(Boolean) : [];
  const out = { vyplnenych: 0, celkem: 0, sekce: {} };
  for (const s of seznam) {
    const pole = relevantniPoleSekce(s, odpovedi);
    const vyplnenych = pole.filter((p) => jeVyplneno((odpovedi || {})[p.id])).length;
    out.sekce[s.id] = { vyplnenych, celkem: pole.length };
    out.vyplnenych += vyplnenych;
    out.celkem += pole.length;
  }
  return out;
}

/**
 * Procenta vyplnění, nebo null když není z čeho počítat.
 * Vymyšlené „0 %“ u dotazníku bez jediného relevantního pole by lhalo.
 */
export function procenta(postup) {
  const celkem = Number(postup?.celkem);
  const vyplnenych = Number(postup?.vyplnenych);
  if (!Number.isFinite(celkem) || celkem <= 0) return null;
  if (!Number.isFinite(vyplnenych) || vyplnenych < 0) return null;
  return Math.round((Math.min(vyplnenych, celkem) / celkem) * 100);
}
